import React, { useState } from 'react'
import { useModal, create, muiDialogV5 } from '@ebay/nice-modal-react'
import { Box, Button, CircularProgress, IconButton, Stack, Tooltip, Typography, styled } from '@mui/material'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import { toast } from 'react-toastify'
import ElectricalServicesIcon from '@mui/icons-material/ElectricalServices'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { commify } from 'ethers/lib/utils'
import FeeButtonGroup from './FeeButtonGroup'
import Dialog from '@/components/common/Dialog'
import useDepositAddressByBtc from '@/hooks/useDepositAddressByBtc'
import NumberInput from '@/components/common/NumberInput'
import { MaxButton } from '@/components/common/MaxButton'
import useUnisatInscribeAndTransfer from '@/hooks/useUnisatInscribeAndTransfer'
import { useBrcTokenBalance } from '@/hooks/useBrcTokenBalance'
import useTokenDepositConfig from '@/hooks/useTokenDepositConfig'

const Title = styled(Box)`
  width: 100%;
  background: var(--ps-primary_bg);
  border: 1px solid rgba(191, 255, 11, 0.2);
  padding: 18px 20px;
  word-break: break-word;
`
const Tag = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;

  padding: 2px 4px;

  background: rgba(191, 255, 11, 0.1);
  border-radius: 4px;

  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  letter-spacing: 0.01em;
  font-feature-settings: 'ss01' on, 'case' on, 'calt' off;
  color: #ffffff;

  margin-right: 4px;
`

export const MinimumAmountWrapper = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  column-gap: 8px;

  margin-top: 10px;
`
export const Text3 = styled(Typography)`
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  color: #ffffff;
`

interface UnisatTransferBrc20DialogProps {
  tokenName: string
}

const UnisatTransferBrc20Dialog = ({ tokenName }: UnisatTransferBrc20DialogProps) => {
  const modal = useModal()

  const { data: depositAddress } = useDepositAddressByBtc()

  const [amountInput, setAmountInput] = useState<string>('')
  const [feeRateValue, setFeeRateValue] = useState('0')

  const {
    data: brcTokenInfoOfUser,
    loading: isLoadingBrc20OfUser,
    refresh: refreshBrc20OfUser,
  } = useBrcTokenBalance(tokenName)

  const { runAsync: inscribeAndTransfer, loading: isInscribeAndTransfering } = useUnisatInscribeAndTransfer({
    sendInscriptionFeeRate: Number(feeRateValue),
  })

  const { data: tokenDepositConfig } = useTokenDepositConfig()
  const depositFeeRate = tokenDepositConfig?.[tokenName] ? tokenDepositConfig?.[tokenName].fee / 10000 : null
  const depositFeeRateInPercent = tokenDepositConfig?.[tokenName]
    ? (tokenDepositConfig?.[tokenName].fee * 100) / 10000
    : null

  return (
    <Dialog {...muiDialogV5(modal)}>
      <Stack sx={{ textAlign: 'left', minWidth: 560 }}>
        <Typography fontSize={32} sx={{ pb: 32 }}>
          Transfer
        </Typography>
        <Stack spacing={8}>
          <Typography fontSize={16}>
            {/* <strong style={{ color: 'var(--ps-primary)' }}>{assetToken}</strong> Deposit  */}
            Address
          </Typography>
          <Title>{depositAddress}</Title>
        </Stack>
        <NumberInput
          fullWidth
          sx={{ mt: 32 }}
          startAdornment={<Tag sx={{ mr: 10 }}>Amount</Tag>}
          endAdornment={
            <MaxButton
              onClick={() => {
                setAmountInput(brcTokenInfoOfUser?.availableBalance || '0')
              }}
            />
          }
          placeholder="0"
          value={`${amountInput}`}
          onUserInput={setAmountInput}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 4, mt: 12 }}>
            <ElectricalServicesIcon sx={{ color: '#BFFF0B' }} />
            <Text3>
              Service fees:{' '}
              {amountInput && typeof depositFeeRate === 'number'
                ? `${commify((Number(amountInput || 0) * depositFeeRate).toFixed(3))} ${tokenName}`
                : '-'}
            </Text3>
            <Tooltip
              title={`Cross-chain service fee will be charged ${
                typeof depositFeeRateInPercent === 'number' ? commify(depositFeeRateInPercent) : '-'
              }%`}
              arrow
            >
              <HelpOutlineIcon sx={{ width: 16, height: 16, opacity: 0.5 }} />
            </Tooltip>
          </Box>

          <MinimumAmountWrapper>
            <Text3 sx={{ opacity: 0.5 }}>Available</Text3>
            <Text3>
              {isLoadingBrc20OfUser ? <CircularProgress size="12px" /> : brcTokenInfoOfUser?.availableBalance || '-'}{' '}
              {tokenName}
            </Text3>
            <IconButton color="primary" size="small" onClick={refreshBrc20OfUser}>
              <AutorenewIcon />
            </IconButton>
          </MinimumAmountWrapper>
        </Box>

        <Typography fontSize={16} sx={{ mt: 20 }}>
          Transfer Fee
        </Typography>
        <FeeButtonGroup feeRate={feeRateValue} onChange={setFeeRateValue} />

        <Box sx={{ pt: 32 }}>
          <Button
            fullWidth
            size="large"
            variant="contained"
            sx={{
              fontWeight: 700,
            }}
            disabled={isInscribeAndTransfering || Number(feeRateValue) <= 0}
            onClick={() => {
              inscribeAndTransfer(tokenName, `${amountInput}`, depositAddress)
                .then(() => {
                  toast.success('The application has been submitted, the transaction is pending confirmation.')
                  modal.hide()
                })
                .catch((err) => {
                  if (err?.message) {
                    toast.error(err?.message)
                  }
                })
            }}
          >
            {isInscribeAndTransfering && (
              <>
                <CircularProgress size={14} />
                &nbsp;
              </>
            )}
            Confirm
          </Button>
        </Box>
      </Stack>
    </Dialog>
  )
}

export default create(UnisatTransferBrc20Dialog)
